import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  FaUser,
  FaLock,
  FaEye,
  FaEyeSlash,
  FaArrowRight,
  FaShieldAlt,
} from "react-icons/fa";
import { Helmet } from "react-helmet-async";
import logo from "../../assets/logo.png"; // Nutrivio logo path
import axiosInstance from "../../api/axiosInstance";
import "./AdminSignIn.css";

export default function AdminSignIn() {
  const [formData, setFormData] = useState({
    email: "",
    password: "",
  });
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  // Redirect if admin is already logged in
  useEffect(() => {
    const token = sessionStorage.getItem("adminToken");
    if (token) {
      navigate("/admin/dashboard", { replace: true });
    }
  }, [navigate]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
    if (error) setError("");
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");

    if (!formData.email.trim() || !formData.password.trim()) {
      setError("Please fill in both email and password.");
      return;
    }

    setLoading(true);

    try {
      const response = await axiosInstance.post("/api/admin/login", {
        email: formData.email.trim(),
        password: formData.password,
      });

      const { success, token, refreshToken, message } = response.data;

      if (success && token) {
        sessionStorage.setItem("adminToken", token);
        if (refreshToken) {
          sessionStorage.setItem("adminRefreshToken", refreshToken);
        }
        navigate("/admin/dashboard", { replace: true });
      } else {
        setError(message || "Invalid email or password.");
      }
    } catch (err) {
      const errMsg =
        err.response?.data?.message ||
        "Unable to connect to server. Please try again later.";
      setError(errMsg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Admin Sign In | Nutrivio Medical Services</title>
        <meta
          name="description"
          content="Sign in to your Nutrivio Medical Services administration portal."
        />
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      <div className="admin-signin-wrapper">
        {/* Subtle Ambient Background Grids */}
        <div className="admin-signin-ambient-grid"></div>
        <div className="admin-signin-glow-teal"></div>
        <div className="admin-signin-glow-green"></div>

        <div className="admin-signin-card">
          {/* Bigger Logo Section */}
          <div className="admin-signin-logo-container">
            <img
              src={logo}
              alt="Nutrivio Medical Services"
              className="admin-signin-logo"
            />
          </div>

          {/* Clean Header */}
          <div className="admin-signin-header">
            <h2 className="admin-signin-title">Admin Sign In</h2>
            <div className="admin-signin-divider">
              <span className="admin-signin-line"></span>
              <FaShieldAlt className="admin-signin-icon-shield" />
              <span className="admin-signin-line"></span>
            </div>
          </div>

          {/* Form */}
          <form onSubmit={submit} noValidate>
            {/* Email Input */}
            <div className="admin-signin-input-wrapper">
              <label className="admin-signin-label" htmlFor="admin-email">
                Official Email
              </label>
              <div className="admin-signin-input-box">
                <FaUser className="admin-signin-icon" />
                <input
                  id="admin-email"
                  name="email"
                  className="admin-signin-input"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="admin@nutrivio.in"
                  autoComplete="email"
                  disabled={loading}
                  required
                />
              </div>
            </div>

            {/* Password Input */}
            <div className="admin-signin-input-wrapper">
              <label className="admin-signin-label" htmlFor="admin-password">
                Password
              </label>
              <div className="admin-signin-input-box">
                <FaLock className="admin-signin-icon" />
                <input
                  id="admin-password"
                  name="password"
                  className="admin-signin-input"
                  type={showPassword ? "text" : "password"}
                  value={formData.password}
                  onChange={handleChange}
                  placeholder="••••••••••••"
                  autoComplete="current-password"
                  disabled={loading}
                  required
                />
                <button
                  type="button"
                  className="admin-signin-eye-btn"
                  onClick={() => setShowPassword((prev) => !prev)}
                  aria-label={showPassword ? "Hide password" : "Show password"}
                  tabIndex="-1"
                >
                  {showPassword ? <FaEyeSlash /> : <FaEye />}
                </button>
              </div>
            </div>

            {/* Error Display */}
            {error && (
              <div className="admin-signin-error" role="alert">
                <span className="admin-signin-error-dot">●</span>
                <span>{error}</span>
              </div>
            )}

            {/* Submit Button */}
            <button
              className="admin-signin-submit-btn"
              type="submit"
              disabled={loading}
            >
              {loading ? (
                <span>Authenticating...</span>
              ) : (
                <>
                  <span className="admin-signin-btn-text">Sign In Securely</span>
                  <FaArrowRight className="admin-signin-btn-arrow" />
                </>
              )}
            </button>
          </form>

          {/* Footer Note */}
          <div className="admin-signin-card-footer">
            <p>© 2026 Nutrivio Medical Services Pvt. Ltd.</p>
          </div>
        </div>
      </div>
    </>
  );
}